import * as THREE from 'three'
import { asRecord } from './utils'

/**
 * 以“已有值优先”的方式合并记录对象。
 *
 * 设计说明：
 * 1. 被剥离的 wrapper 只作为语义补充来源，不能覆盖 canonical root 上已经确定的值；
 * 2. 对嵌套对象采用递归合并，避免把 `extras.step`、`metadata` 这类分层协议数据整块覆盖；
 * 3. 该函数只处理普通记录对象，不处理数组合并，数组保持 canonical root 现有值优先。
 *
 * @param target 目标记录，会被原地补全缺失字段。
 * @param source 来源记录，只会填充目标中缺失的字段。
 * @returns 合并后的目标记录。
 */
export function mergeRecordFallback(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  Object.entries(source).forEach(([key, value]) => {
    const currentValue = target[key]
    const sourceRecord = asRecord(value)
    const targetRecord = asRecord(currentValue)

    if (sourceRecord && targetRecord) {
      mergeRecordFallback(targetRecord, sourceRecord)
      target[key] = targetRecord
      return
    }

    if (currentValue === undefined) {
      target[key] = value
    }
  })

  return target
}

/**
 * 将被剥离 wrapper 上的协议元数据继承到 canonical root。
 *
 * 设计说明：
 * 1. glTF 导入后，权威 `brep binding plan`、`primitiveEntityEntries` 等协议数据可能挂在被剥离的包装层上；
 * 2. 零件/装配规范化时若不显式继承，这些 metadata 会在 clone 前丢失，最终导致 outline 与点线面交互缺失；
 * 3. 这里统一做“wrapper -> canonical root”的浅层回填，确保后续 sceneTemplate 与 runtime 都只依赖规范化后的根节点。
 *
 * @param canonicalRoot 规范化后的根节点。
 * @param strippedWrappers 被剥离的 wrapper 列表，顺序为从外到内。
 */
export function inheritStrippedRootUserData(
  canonicalRoot: THREE.Object3D,
  strippedWrappers: THREE.Object3D[],
): void {
  if (strippedWrappers.length === 0) {
    return
  }

  const mergedUserData = { ...(asRecord(canonicalRoot.userData) ?? {}) }
  strippedWrappers.forEach((wrapper) => {
    const wrapperUserData = asRecord(wrapper.userData)
    if (!wrapperUserData) {
      return
    }
    mergeRecordFallback(mergedUserData, wrapperUserData)
  })

  canonicalRoot.userData = mergedUserData

  const mergedMetadata = { ...(asRecord(canonicalRoot.metadata) ?? {}) }
  strippedWrappers.forEach((wrapper) => {
    const wrapperMetadata = asRecord(wrapper.metadata)
    if (!wrapperMetadata) {
      return
    }
    mergeRecordFallback(mergedMetadata, wrapperMetadata)
  })

  canonicalRoot.metadata = mergedMetadata
}
